import React, { useState } from 'react';
import type { MobilityRoutine } from '../types';

interface ExpandableComponentMobilityProps {
  type: 'warmup' | 'cooldown';
  mobilityRoutines: MobilityRoutine[];
  onComplete?: (routineId: string) => void;
}

/**
 * 🆕 PHASE 6 PART 1: Expandable Component Mobility
 * 
 * Displays warmup/cooldown mobility sections within workout cards/detail pages.
 * - Collapsible/expandable sections
 * - Shows exercises with sets/reps
 * - Trackable completion (separate from main workout)
 * - Physio integration (shows PT attribution if applicable)
 */
export const ExpandableComponentMobility: React.FC<ExpandableComponentMobilityProps> = ({
  type,
  mobilityRoutines,
  onComplete
}) => {
  // 🐛 DIAGNOSTIC (Jan 21, 2026): Debug props received
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔍 ExpandableComponentMobility PROPS RECEIVED');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('type:', type);
  console.log('mobilityRoutines:', mobilityRoutines);
  console.log('mobilityRoutines.length:', mobilityRoutines.length);
  
  // Check each routine for empty objects at prop level
  mobilityRoutines.forEach((routine, idx) => {
    console.log(`Prop routine #${idx + 1}:`, routine);
    (Object.keys(routine) as Array<keyof typeof routine>).forEach(key => {
      const value = routine[key];
      const isEmptyObject = typeof value === 'object' && !Array.isArray(value) && value !== null && Object.keys(value).length === 0;
      if (isEmptyObject) {
        console.error(`  ⚠️ EMPTY OBJECT IN PROPS at routine.${key}:`, value, '← THIS WILL CAUSE REACT ERROR #31');
      }
    });
  });
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [completedRoutines, setCompletedRoutines] = useState<Set<string>>(new Set());
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);  // 🆕 JAN 31, 2026: Modal for full-size exercise image
  const [expandedExerciseIndex, setExpandedExerciseIndex] = useState<number | null>(null);  // 🆕 JAN 31, 2026: Track which exercise is expanded

  // 🆕 PHASE 11 SUB-SPRINT (APR 19, 2026) — Warmup/Cooldown Faithful Projection.
  // Structural `component_role` is the source of truth when present. Title-keyword
  // matching is the dual-read fallback for legacy rows only. Mirrors the native
  // ExpandableComponentMobility filter to keep web + native in sync.
  const typeRoutines = mobilityRoutines.filter(routine => {
    if (routine.context !== 'component') return false;

    // 🎯 Structural read first
    const role = (routine as any).component_role;
    if (role === 'warmup') return type === 'warmup';
    if (role === 'cooldown') return type === 'cooldown';

    // ── Fallback (legacy rows with component_role=null) ──
    const title = (routine.title || '').toLowerCase();

    // Enhanced warmup detection
    if (type === 'warmup') {
      return title.includes('warmup') ||
             title.includes('warm-up') ||
             title.includes('warm up') ||
             title.includes('pre-run') ||
             title.includes('pre-workout') ||
             title.includes('activation') ||
             title.includes('prep') ||
             title.includes('dynamic');
    }

    // Enhanced cooldown detection (FIX: includes "stretch", "recovery")
    if (type === 'cooldown') {
      return title.includes('cooldown') ||
             title.includes('cool-down') ||
             title.includes('cool down') ||
             title.includes('post-run') ||
             title.includes('post-workout') ||
             title.includes('recovery stretch') ||
             title.includes('static stretch') ||
             title.includes('stretch') ||  // Catches "Post-Run Recovery Stretch"
             title.includes('recovery') &&  !title.includes('recovery run');  // Exclude "recovery run" workouts
    }

    return false;
  });

  // 🐛 FEB 3, 2026 (Bug #72): DETAILED DIAGNOSTIC for missing exercises/poses
  console.log(`🔍 Checking ${typeRoutines.length} ${type} routines for exercises...`);
  typeRoutines.forEach((routine, idx) => {
    console.log(`  Routine ${idx + 1}:`, routine.title);
    console.log(`    routine.exercises:`, routine.exercises, 'isArray:', Array.isArray(routine.exercises), 'length:', routine.exercises?.length);
    console.log(`    routine.poses:`, routine.poses, 'isArray:', Array.isArray(routine.poses), 'length:', routine.poses?.length);
    console.log(`    _debug fields:`, routine._debug_routine_data, routine._debug_poses_count, routine._debug_exercises_count);
    console.log(`    All keys:`, Object.keys(routine));
  });
  
  const routinesWithExercises = typeRoutines.filter(routine => {
    const exercises = routine.exercises ||  // ✅ Check top-level exercises
                     routine.poses ||       // ✅ Check top-level poses
                     [];
    return Array.isArray(exercises) && exercises.length > 0;
  });

  if (routinesWithExercises.length === 0) {
    console.error(`❌ ExpandableComponentMobility: No ${type} routines with exercises found after filtering`);
    console.error(`   typeRoutines count: ${typeRoutines.length}, routinesWithExercises count: 0`);
    return null; // Don't show section if no exercise data
  }
  
  console.log(`🔍 ExpandableComponentMobility: Filtered to ${routinesWithExercises.length} ${type} routines with exercises`);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleRoutineComplete = (routineId: string) => {
    setCompletedRoutines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routineId)) {
        newSet.delete(routineId);
      } else {
        newSet.add(routineId);
      }
      return newSet;
    });

    if (onComplete) {
      onComplete(routineId);
    }
  };

  // Calculate total duration with type safety
  // Parse duration string (e.g., "15 minutes") to extract minutes
  const parseDurationMinutes = (durationStr: string): number => {
    const match = durationStr.match(/(\d+)\s*(?:min|minute|minutes)/i);
    return match ? parseInt(match[1], 10) : 0;
  };
  
  // 🆕 JAN 31, 2026: Use routinesWithExercises (filtered list)
  const totalDuration = routinesWithExercises.reduce((sum, routine) => {
    // Check both duration field and duration_minutes
    const duration = routine.duration_minutes || 
                     (typeof routine.duration === 'string' ? parseDurationMinutes(routine.duration) : 0);
    return sum + duration;
  }, 0);

  return (
    <>
    <div className="border-t border-gray-200 mt-4">
      {/* Header - Collapsible */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            type === 'warmup' ? 'bg-orange-100' : 'bg-blue-100'
          }`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {type === 'warmup' ? (
                // Fire icon for warmup
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
              ) : (
                // Snowflake icon for cooldown
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              )}
            </svg>
          </div>

          {/* Title and Duration */}
          <div>
            <h3 className="font-semibold text-gray-900 capitalize">{type}</h3>
            <p className="text-sm text-gray-500">
              {totalDuration || 0} min • {routinesWithExercises.length} routine{routinesWithExercises.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 bg-gray-50">
          {(() => {
            console.log('🔍 ExpandableComponentMobility RENDER: About to map routinesWithExercises');
            console.log('  routinesWithExercises.length:', routinesWithExercises.length);
            console.log('  routinesWithExercises:', routinesWithExercises);
            
            return routinesWithExercises.map(routine => {
              console.log('🔍 ExpandableComponentMobility: Rendering routine:', routine.id);
              console.log('  routine object:', routine);
              
              // Scan for empty objects in routine
              (Object.keys(routine) as Array<keyof typeof routine>).forEach(key => {
                const value = routine[key];
                const isEmptyObject = typeof value === 'object' && !Array.isArray(value) && value !== null && Object.keys(value).length === 0;
                if (isEmptyObject) {
                  console.error(`  ⚠️ EMPTY OBJECT at routine.${key}:`, value, '← THIS WILL CAUSE REACT ERROR #31');
                }
              });
              
              console.log('  routine.title:', routine.title, 'type:', typeof routine.title);
              console.log('  routine.duration:', routine.duration, 'type:', typeof routine.duration);
              
              const isCompleted = completedRoutines.has(routine.id);
              const isPhysio = routine.title?.includes('PT:') || routine.title?.includes('Physio') || routine.type === 'corrective';

              return (
                <div 
                  key={routine.id} 
                  className={`bg-white rounded-lg p-4 border ${isCompleted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                >
                  {/* Routine Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">
                        {String(routine.title || 'Mobility Exercise')}
                      {isPhysio && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          PT
                        </span>
                      )}
                    </h4>
                    {routine.description && typeof routine.description === 'string' && (
                      <p className="text-sm text-gray-600">{routine.description}</p>
                    )}
                  </div>

                  {/* Completion Checkbox */}
                  <button
                    onClick={() => handleRoutineComplete(routine.id)}
                    className="flex-shrink-0 ml-2"
                  >
                    {isCompleted ? (
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                    )}
                  </button>
                </div>

                {/* Exercises - Check poses (primary) and exercises */}
                {/* 🐛 FEB 2, 2026 (Bug #59): Use top-level fields (routine_data not mapped!) */}
                {(() => {
                  // ✅ Frontend mapping extracts to top level (dataService.ts lines 261-272)
                  const exercises = routine.exercises ||  // Top-level exercises
                                   routine.poses ||       // Top-level poses
                                   [];
                  
                  console.log(`🔍 Rendering exercises for ${routine.title}:`, exercises.length);
                  
                  if (!Array.isArray(exercises) || exercises.length === 0) {
                    console.warn(`⚠️ No exercises found for ${routine.title}`);
                    return null;
                  }
                  
                  return (
                    <div className="space-y-3 mt-3">
                      {exercises.map((exercise: any, idx: number) => {
                        // 🆕 JAN 31, 2026: Get image URL from multiple possible fields
                        const imageUrl = exercise.image_url || 
                                        (exercise.media_urls && exercise.media_urls.length > 0 ? exercise.media_urls[0] : null) ||
                                        (exercise.images && exercise.images.length > 0 ? exercise.images[0] : null);
                        
                        return (
                          <div 
                            key={idx} 
                            className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => {
                              // Toggle exercise expansion
                              setExpandedExerciseIndex(expandedExerciseIndex === idx ? null : idx);
                            }}
                          >
                            <span className="text-gray-400 font-medium">{idx + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-900 font-medium">{String(exercise.name || exercise.exercise_name || 'Exercise')}</span>
                              {((typeof exercise.sets === 'number' && exercise.sets > 0) || (typeof exercise.reps === 'number' && exercise.reps > 0) || (typeof exercise.reps === 'string' && exercise.reps)) && (
                                <span className="text-gray-500 ml-2 text-xs">
                                  {typeof exercise.sets === 'number' && exercise.sets > 0 && `${exercise.sets} sets`}
                                  {typeof exercise.sets === 'number' && exercise.sets > 0 && (typeof exercise.reps === 'number' || typeof exercise.reps === 'string') && exercise.reps && ' × '}
                                  {((typeof exercise.reps === 'number' && exercise.reps > 0) || (typeof exercise.reps === 'string' && exercise.reps)) && `${exercise.reps} reps`}
                                </span>
                              )}
                              
                              {/* Instructions — always visible when present */}
                              {exercise.instructions && typeof exercise.instructions === 'string' && (
                                <p className="text-xs text-gray-600 mt-1 leading-relaxed">{exercise.instructions}</p>
                              )}

                              {/* Form Tips — shown when expanded, all cues (no cap) */}
                              {expandedExerciseIndex === idx && exercise.form_cues && exercise.form_cues.length > 0 && (
                                <div className="mt-2 pl-4 border-l-2 border-teal-300 space-y-1">
                                  <p className="text-xs font-semibold text-teal-700 mb-1">Form Tips</p>
                                  {exercise.form_cues.map((cue: string, cueIdx: number) => (
                                    <p key={cueIdx} className="text-xs text-gray-600">• {cue}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* 🆕 JAN 31, 2026: Exercise image on right (if available) */}
                            {imageUrl && (
                              <div 
                                className="flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();  // Prevent exercise toggle
                                  // 🎯 USER REQUEST: If collapsed, expand THEN show image. If expanded, just show image.
                                  if (expandedExerciseIndex !== idx) {
                                    setExpandedExerciseIndex(idx);  // Expand exercise
                                  }
                                  setFullscreenImage(imageUrl);  // Always show fullscreen
                                }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={exercise.name || 'Exercise'}
                                  className="w-16 h-16 object-cover rounded-lg border border-gray-300"
                                  onError={(e) => {
                                    // Fallback: Hide image if fails to load
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Duration */}
                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                  Duration: {typeof routine.duration === 'string' ? routine.duration : '5 minutes'}
                </div>
              </div>
            );
          });
          })()}
        </div>
      )}
    </div>
    
    {/* 🆕 JAN 31, 2026: Fullscreen Image Modal (same as ExpandablePhaseSection) */}
    {fullscreenImage && (
      <div 
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={() => setFullscreenImage(null)}
      >
        <div className="relative max-w-4xl max-h-full">
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"
            onClick={() => setFullscreenImage(null)}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Full-Size Image */}
          <img 
            src={fullscreenImage} 
            alt="Exercise demonstration"
            className="w-full h-auto rounded-lg max-h-screen object-contain"
            onClick={(e) => e.stopPropagation()}  // Prevent closing when clicking image
          />
        </div>
      </div>
    )}
  </>
  );
};





